import { SelectedEquipment } from '../RaptorJobCreateModal';
import { OptionType } from './useEquipmentSelector';
import { EquipmentTableData } from '@/equipments/application/types/EquipmentTypes';

interface UseEquipmentSelectorFiltersProps {
  availableClasses: OptionType[];
  availableTypes: OptionType[];
  availableSizes: OptionType[];
  selectedClass: OptionType | null;
  selectedType: OptionType | null;
  selectedSize: OptionType | null;
  setSelectedClass: (classOption: OptionType | null) => void;
  setSelectedType: (typeOption: OptionType | null) => void;
  setSelectedSize: (sizeOption: OptionType | null) => void;
  setSelectedEquipment: (equipmentOption: SelectedEquipment | null) => void;
  equipments: EquipmentTableData[];
}
export const useEquipmentSelectorFilters = ({
  availableClasses,
  availableTypes,
  availableSizes,
  selectedClass,
  selectedType,
  selectedSize,
  equipments,
  setSelectedClass,
  setSelectedType,
  setSelectedSize,
  setSelectedEquipment
}: UseEquipmentSelectorFiltersProps) => {
  const getFilteredTypeOrSizeOptionsByClass = (
    classId: string | number | null | undefined,
    availableOptions: OptionType[],
    propertyExtractor: (equipment: {
      class?: { id?: number | null } | null;
      type?: { id?: number | null } | null;
      size?: { id?: number | null } | null;
    }) => number | null | undefined
  ): OptionType[] => {
    if (!classId) {
      return availableOptions;
    }

    const optionIds = new Set(
      equipments
        ?.filter((equipment) => equipment.class?.id === classId)
        .map(propertyExtractor)
        .filter((id): id is number => id != null)
    );

    return availableOptions.filter((option) => optionIds.has(Number(option.id)));
  };

  const filteredClasses = availableClasses;

  const filteredTypes = getFilteredTypeOrSizeOptionsByClass(selectedClass?.id, availableTypes, (equipment) => equipment.type?.id);

  const filteredSizes = getFilteredTypeOrSizeOptionsByClass(selectedClass?.id, availableSizes, (equipment) => equipment.size?.id);

  const handleClassChange = (newClass: OptionType | null) => {
    setSelectedClass(newClass);

    if (newClass?.id) {
      const newFilteredTypes = getFilteredTypeOrSizeOptionsByClass(newClass.id, availableTypes, (eq) => eq.type?.id);
      const newFilteredSizes = getFilteredTypeOrSizeOptionsByClass(newClass.id, availableSizes, (eq) => eq.size?.id);

      if (selectedType?.id && !newFilteredTypes.some((type) => type.id === selectedType.id)) {
        setSelectedType(null);
      }
      if (selectedSize?.id && !newFilteredSizes.some((size) => size.id === selectedSize.id)) {
        setSelectedSize(null);
      }
    }
  };

  const handleTypeChange = (newType: OptionType | null) => {
    setSelectedType(newType);
  };

  const handleSizeChange = (newSize: OptionType | null) => {
    setSelectedSize(newSize);
  };

  const handleEquipmentSelect = (equipment: SelectedEquipment) => {
    setSelectedEquipment(equipment);

    if (equipment.class?.id && equipment.class?.name) {
      setSelectedClass({ id: equipment.class.id, name: equipment.class.name });
    }
    if (equipment.type?.id && equipment.type?.name) {
      setSelectedType({ id: equipment.type.id, name: equipment.type.name });
    }
    if (equipment.size?.id && equipment.size?.name) {
      setSelectedSize({ id: equipment.size.id, name: equipment.size.name });
    }
  };

  const handleEquipmentDeselect = () => {
    setSelectedEquipment(null);
  };

  const handleClearFilters = () => {
    setSelectedClass(null);
    setSelectedType(null);
    setSelectedSize(null);
  };

  return {
    filteredClasses,
    filteredTypes,
    filteredSizes,
    handleClassChange,
    handleTypeChange,
    handleSizeChange,
    handleEquipmentSelect,
    handleEquipmentDeselect,
    handleClearFilters
  };
};
